// Generated from docs/legal/source by frontend/scripts/generate-legal.cjs.
// Approved edits: provider typo, removed internal note, and guest disclosures from docs/legal/guest-amendments.json.
export const legalContent = {
  "privacy": {
    "title": "Privacy Policy",
    "preamble": [
      {
        "type": "paragraph",
        "text": "**Effective date:** October 8, 2026"
      },
      {
        "type": "paragraph",
        "text": "CubitPlay is operated by **CubitPlay**, based in **India/Kerala State**."
      },
      {
        "type": "paragraph",
        "text": "This Privacy Policy explains what information CubitPlay collects, why we use it, when information may be public, and the choices available to you."
      },
      {
        "type": "paragraph",
        "text": "Our approach is simple: **you should be able to play without sharing more than is necessary.**"
      }
    ],
    "sections": [
      {
        "number": 1,
        "title": "Information We Collect",
        "blocks": [
          {
            "type": "paragraph",
            "text": "You can play CubitPlay without creating an account."
          },
          {
            "type": "paragraph",
            "text": "When you first submit a valid official Daily Slice cut without a completed account profile, we create a browser-bound guest profile with a random identifier, a generated friendly public nickname, and a creation timestamp. We do not ask for a name or email address for this guest profile."
          },
          {
            "type": "paragraph",
            "text": "Depending on how you use CubitPlay, we may process:"
          },
          {
            "type": "list",
            "items": [
              "gameplay information such as scores, attempts, challenge results, and accuracy;",
              "information stored locally on your device to remember game progress and preferences;",
              "your email address if you create a CubitPlay account;",
              "your CubitPlay public nickname;",
              "authentication and session information;",
              "technical and security information needed to operate and protect the service; and",
              "analytics information where you have chosen to allow analytics."
            ]
          },
          {
            "type": "paragraph",
            "text": "We aim to collect only information reasonably necessary to provide, secure, understand, and improve CubitPlay."
          }
        ]
      },
      {
        "number": 2,
        "title": "CubitPlay Accounts",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Creating an account is optional."
          },
          {
            "type": "paragraph",
            "text": "If you create a CubitPlay account, we use your email address to authenticate you and associate your saved game history with your account."
          },
          {
            "type": "paragraph",
            "text": "Your account may be used across multiple CubitPlay games."
          },
          {
            "type": "paragraph",
            "text": "We also maintain a public CubitPlay nickname for features such as leaderboards. We encourage you to use a nickname rather than your real name."
          },
          {
            "type": "paragraph",
            "text": "We do not ask you to provide a profile photo, phone number, school, gender, biography, or precise location as part of the current account experience."
          }
        ]
      },
      {
        "number": 3,
        "title": "Gameplay and Game History",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay may process gameplay information including:"
          },
          {
            "type": "list",
            "items": [
              "scores;",
              "accuracy and amount off target;",
              "challenge and round identifiers;",
              "attempts;",
              "completion times;",
              "game-history records;",
              "cut or interaction data needed to validate an official result; and",
              "local records such as personal bests and shape history."
            ]
          },
          {
            "type": "paragraph",
            "text": "Some Endless-mode information may remain only on your device."
          },
          {
            "type": "paragraph",
            "text": "When you are signed in, supported game results may be saved to your CubitPlay account."
          },
          {
            "type": "paragraph",
            "text": "For official challenges such as Daily Slice, CubitPlay may send interaction data to the server so the result can be validated and attempt limits can be enforced."
          },
          {
            "type": "paragraph",
            "text": "Guest Daily results, attempts, completion history and streaks are stored on our servers and linked to the guest profile. The browser identifier lets the same browser access that history on later visits. Saving the guest profile to an authenticated account links eligible history to that account for access across devices; existing account results take precedence where both profiles have attempts for the same challenge."
          }
        ]
      },
      {
        "number": 4,
        "title": "Public Leaderboards",
        "blocks": [
          {
            "type": "paragraph",
            "text": "If you participate in an official leaderboard, some game information is public."
          },
          {
            "type": "paragraph",
            "text": "This may include:"
          },
          {
            "type": "list",
            "items": [
              "your CubitPlay nickname;",
              "your score;",
              "your accuracy or amount off target;",
              "your rank; and",
              "the challenge or game associated with the result."
            ]
          },
          {
            "type": "paragraph",
            "text": "Your account email address is **not** displayed on public leaderboards."
          },
          {
            "type": "paragraph",
            "text": "Completed, server-verified Daily results from guest profiles can appear on public leaderboards under the generated nickname, just like account results. An account is not required to participate in the public Daily leaderboard."
          }
        ]
      },
      {
        "number": 5,
        "title": "Authentication",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay currently uses **Supabase Auth** to create and manage account sessions."
          },
          {
            "type": "paragraph",
            "text": "Sign-in uses a one-time code sent to the email address you provide."
          },
          {
            "type": "paragraph",
            "text": "**Resend** is used to deliver authentication emails."
          },
          {
            "type": "paragraph",
            "text": "Authentication providers may process information such as your email address, session information, technical request information, and security logs as needed to provide and protect the authentication service."
          }
        ]
      },
      {
        "number": 6,
        "title": "Cookies, Local Storage, and Similar Technologies",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay uses browser storage technologies such as cookies and local storage for essential functions, including:"
          },
          {
            "type": "list",
            "items": [
              "keeping you signed in;",
              "remembering gameplay progress and preferences;",
              "storing local game records;",
              "protecting requests;",
              "remembering consent choices; and",
              "supporting pending or recently completed game activity."
            ]
          },
          {
            "type": "paragraph",
            "text": "Some game information may remain in your browser even after you sign out."
          },
          {
            "type": "paragraph",
            "text": "An essential, first-party guest cookie holds a random signed credential used to recognize your guest profile; the server stores a protected hash of that credential. The cookie can last up to one year and is renewed when you return. Clearing browser data, losing or blocking the cookie, or using another browser/device may create a new guest profile and remove your access to the old one. We do not use browser fingerprinting to recognize or recover guest profiles."
          },
          {
            "type": "paragraph",
            "text": "You can remove local information using your browser controls, although doing so may reset local progress or preferences."
          },
          {
            "type": "heading",
            "text": "Optional analytics"
          },
          {
            "type": "paragraph",
            "text": "With your permission, CubitPlay may also use analytics technologies."
          },
          {
            "type": "paragraph",
            "text": "Where consent is required, optional analytics should remain disabled until you choose to allow them."
          },
          {
            "type": "paragraph",
            "text": "You can review or change your choice through the site's **Cookie settings** control."
          }
        ]
      },
      {
        "number": 7,
        "title": "Analytics",
        "blocks": [
          {
            "type": "paragraph",
            "text": "With your permission, we use **Google Analytics** to understand how people find and use CubitPlay and how our games perform."
          },
          {
            "type": "paragraph",
            "text": "Analytics may include information such as:"
          },
          {
            "type": "list",
            "items": [
              "pages or games visited;",
              "general interaction and gameplay events;",
              "device and browser information;",
              "approximate geographic information derived from technical data; and",
              "referral or traffic-source information."
            ]
          },
          {
            "type": "paragraph",
            "text": "We use this information to understand usage patterns, troubleshoot problems, and improve CubitPlay."
          },
          {
            "type": "paragraph",
            "text": "We do not currently use CubitPlay account or gameplay information for targeted advertising."
          },
          {
            "type": "paragraph",
            "text": "If our advertising or analytics practices materially change, we will update this Policy and any required consent controls."
          }
        ]
      },
      {
        "number": 8,
        "title": "Service Providers",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We use third-party service providers to operate CubitPlay."
          },
          {
            "type": "paragraph",
            "text": "Current providers include:"
          },
          {
            "type": "list",
            "items": [
              "**Supabase** — authentication, database, and related backend services;",
              "**Resend** — delivery of authentication emails;",
              "**Google Analytics** — optional website and gameplay analytics; and",
              "**Emergent** — website hosting, deployment, or infrastructure."
            ]
          },
          {
            "type": "paragraph",
            "text": "These providers may process information on our behalf under their own security and privacy obligations."
          },
          {
            "type": "paragraph",
            "text": "Their processing locations and subprocessors may change over time."
          }
        ]
      },
      {
        "number": 9,
        "title": "Children and Account Eligibility",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay games can be played without an account."
          },
          {
            "type": "paragraph",
            "text": "CubitPlay accounts are currently intended for people aged **18 or older**. If you are under 18, please do not create a CubitPlay account. You may continue to use game features that do not require an account."
          },
          {
            "type": "paragraph",
            "text": "We do not ask players who use CubitPlay without an account to provide a name or email address."
          },
          {
            "type": "paragraph",
            "text": "CubitPlay does not currently offer chat, comments, public profile photos, or direct messaging."
          },
          {
            "type": "paragraph",
            "text": "If you believe that a person under 18 has created an account or provided personal information to us contrary to this rule, please contact us at **privacy@cubitplay.com** so we can review the situation."
          }
        ]
      },
      {
        "number": 10,
        "title": "Why We Use Information",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We use information described in this Policy to:"
          },
          {
            "type": "list",
            "items": [
              "provide CubitPlay and its games;",
              "authenticate accounts;",
              "save game history where supported;",
              "calculate and verify official results;",
              "operate leaderboards;",
              "enforce game rules and prevent abuse;",
              "maintain security and troubleshoot problems;",
              "remember preferences and local progress;",
              "understand and improve CubitPlay where analytics is permitted;",
              "respond to support and privacy requests; and",
              "comply with legal obligations."
            ]
          }
        ]
      },
      {
        "number": 11,
        "title": "Data Retention",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We keep information only for as long as reasonably necessary for the purposes described in this Policy, including providing accounts and game history, maintaining security, preventing fraud or abuse, resolving disputes, and meeting legal obligations."
          },
          {
            "type": "paragraph",
            "text": "In general:"
          },
          {
            "type": "list",
            "items": [
              "local browser data remains until it expires, is replaced, or you clear it;",
              "account information and saved game history may be kept while your account remains active;",
              "public leaderboard records may remain while associated game history is retained;",
              "authentication, security, server, and provider logs may be retained for limited operational or security periods; and",
              "backups may retain deleted information temporarily until normal backup rotation removes it."
            ]
          },
          {
            "type": "paragraph",
            "text": "Where practical, we delete or de-identify information when it is no longer needed."
          },
          {
            "type": "paragraph",
            "text": "Clearing a guest cookie does not itself delete server-side guest history or its public results. Guest history is retained for the game-history, leaderboard, security and retention purposes described above. Contact privacy@cubitplay.com for privacy requests; access to the original browser profile may be needed to verify a request."
          }
        ]
      },
      {
        "number": 12,
        "title": "Account Deletion",
        "blocks": [
          {
            "type": "paragraph",
            "text": "You can request or initiate deletion of your CubitPlay account using the available account settings."
          },
          {
            "type": "paragraph",
            "text": "Account deletion is intended to remove your CubitPlay profile and associated saved game history and public leaderboard entries from active systems."
          },
          {
            "type": "paragraph",
            "text": "Some information may remain temporarily in backups, security logs, fraud-prevention records, or provider systems where reasonably necessary or legally required."
          },
          {
            "type": "paragraph",
            "text": "Deleting your CubitPlay account may not automatically erase game data stored locally in your browser. You can clear that information using your browser controls."
          }
        ]
      },
      {
        "number": 13,
        "title": "Your Choices and Rights",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Depending on where you live, you may have rights relating to your personal information, such as rights to:"
          },
          {
            "type": "list",
            "items": [
              "access information we hold about you;",
              "correct inaccurate information;",
              "request deletion;",
              "object to or restrict certain processing;",
              "withdraw consent where processing is based on consent; or",
              "receive a copy of certain information."
            ]
          },
          {
            "type": "paragraph",
            "text": "You can also:"
          },
          {
            "type": "list",
            "items": [
              "play CubitPlay without creating an account;",
              "decline optional analytics where offered;",
              "change analytics preferences through Cookie settings;",
              "clear browser storage;",
              "sign out; and",
              "delete your account."
            ]
          },
          {
            "type": "paragraph",
            "text": "To make a privacy request, contact **privacy@cubitplay.com**."
          },
          {
            "type": "paragraph",
            "text": "We may need to verify your identity before completing certain requests."
          }
        ]
      },
      {
        "number": 14,
        "title": "Security",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We use reasonable technical and organizational measures designed to protect CubitPlay and the information processed through it."
          },
          {
            "type": "paragraph",
            "text": "These measures may include access controls, protected server-side credentials, authentication safeguards, encrypted connections, and server-side validation of sensitive game actions."
          },
          {
            "type": "paragraph",
            "text": "No online service can guarantee absolute security."
          },
          {
            "type": "paragraph",
            "text": "If you believe your account or information may have been compromised, contact us at **privacy@cubitplay.com**."
          }
        ]
      },
      {
        "number": 15,
        "title": "International Processing",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay and its service providers may process information in countries other than the country where you live."
          },
          {
            "type": "paragraph",
            "text": "Where required by applicable law, we will use appropriate safeguards for international transfers of personal information."
          }
        ]
      },
      {
        "number": 16,
        "title": "Changes to This Policy",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We may update this Privacy Policy as CubitPlay changes or as our legal, technical, or operational requirements evolve."
          },
          {
            "type": "paragraph",
            "text": "When we make material changes, we will update the effective date above and may provide additional notice where appropriate."
          }
        ]
      },
      {
        "number": 17,
        "title": "Contact",
        "blocks": [
          {
            "type": "paragraph",
            "text": "For privacy questions or requests:"
          },
          {
            "type": "paragraph",
            "text": "**privacy@cubitplay.com**"
          },
          {
            "type": "paragraph",
            "text": "For general CubitPlay questions:"
          },
          {
            "type": "paragraph",
            "text": "**hello@cubitplay.com**"
          },
          {
            "type": "paragraph",
            "text": "Legal operator:"
          },
          {
            "type": "paragraph",
            "text": "**CubitPlay**  \n**India/Kerala State**"
          }
        ]
      }
    ]
  },
  "terms": {
    "title": "Terms of Use",
    "preamble": [
      {
        "type": "paragraph",
        "text": "**Effective date:** October 8, 2026"
      },
      {
        "type": "paragraph",
        "text": "These Terms of Use govern your use of CubitPlay, its games, accounts, leaderboards, and related features available through **cubitplay.com**."
      },
      {
        "type": "paragraph",
        "text": "CubitPlay is operated by **CubitPlay**, based in **India/Kerala State**."
      },
      {
        "type": "paragraph",
        "text": "By accessing or using CubitPlay, you agree to these Terms. If you are not legally able to agree to these Terms yourself, you should use CubitPlay only with the permission of a parent or legal guardian."
      }
    ],
    "sections": [
      {
        "number": 1,
        "title": "Eligibility",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay games can be played without an account."
          },
          {
            "type": "paragraph",
            "text": "CubitPlay accounts are currently available only to people aged **18 or older**. If you are under 18, you may continue to play features that do not require an account, but you should not create a CubitPlay account."
          },
          {
            "type": "paragraph",
            "text": "Additional age or eligibility rules may apply where required by local law."
          }
        ]
      },
      {
        "number": 2,
        "title": "Accounts",
        "blocks": [
          {
            "type": "paragraph",
            "text": "A CubitPlay account works across CubitPlay games and is not limited to a single game."
          },
          {
            "type": "paragraph",
            "text": "Account sign-in currently uses a one-time code sent to your email address. You are responsible for keeping access to your email account and sign-in codes secure."
          },
          {
            "type": "paragraph",
            "text": "You must provide accurate information when creating or using an account and must not create accounts for fraudulent, abusive, or deceptive purposes."
          },
          {
            "type": "paragraph",
            "text": "You may delete your account through the available account settings."
          }
        ]
      },
      {
        "number": 3,
        "title": "Public Nicknames",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Your CubitPlay nickname is public and may appear on leaderboards and other game features."
          },
          {
            "type": "paragraph",
            "text": "Players without an account may receive a generated public guest nickname when they first participate in an official Daily Slice. Creating an account is not required to appear on the Daily leaderboard."
          },
          {
            "type": "paragraph",
            "text": "Please do not use your email address, phone number, full legal name, or other sensitive personal information as your nickname."
          },
          {
            "type": "paragraph",
            "text": "We may reject, change, or remove nicknames that are offensive, impersonating, deceptive, contain personal or contact information, infringe the rights of others, or otherwise violate these Terms."
          },
          {
            "type": "paragraph",
            "text": "Changing your nickname does not create a new account or erase your game history."
          }
        ]
      },
      {
        "number": 4,
        "title": "Acceptable Use",
        "blocks": [
          {
            "type": "paragraph",
            "text": "You may use CubitPlay only for lawful, personal use and in a way that does not interfere with the service or other players."
          },
          {
            "type": "paragraph",
            "text": "You must not:"
          },
          {
            "type": "list",
            "items": [
              "attempt unauthorized access to CubitPlay systems or other accounts;",
              "interfere with, disrupt, overload, or damage the service;",
              "abuse authentication or security features;",
              "use automated tools to manipulate gameplay or leaderboards unfairly;",
              "forge, alter, or falsify scores or game results;",
              "bypass attempt limits or other gameplay restrictions;",
              "use CubitPlay to distribute malicious code or unlawful content; or",
              "use the service in a way that violates applicable law or the rights of others."
            ]
          }
        ]
      },
      {
        "number": 5,
        "title": "Perfect Slice Gameplay",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Perfect Slice asks players to make one straight cut toward a target percentage."
          },
          {
            "type": "paragraph",
            "text": "In Endless mode, rounds may be played repeatedly. A round currently gives up to three attempts, and the best result counts for that round."
          },
          {
            "type": "paragraph",
            "text": "Some gameplay records may remain only on your device unless you are signed in and the relevant feature supports account-based saving."
          },
          {
            "type": "paragraph",
            "text": "Gameplay rules may evolve as CubitPlay improves or introduces new games and modes."
          }
        ]
      },
      {
        "number": 6,
        "title": "Daily Slice",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Daily Slice gives participating players the same official challenge for a particular UTC day."
          },
          {
            "type": "paragraph",
            "text": "Each Daily Slice currently allows up to **three valid scored attempts**, and your best valid attempt counts."
          },
          {
            "type": "paragraph",
            "text": "Daily attempt limits are enforced on the server for each account or browser-bound guest identity. Guest history and Daily streaks can continue on the same browser using an essential identifier; a guest identity is not a guarantee that a player is a unique person. The prohibitions on bypassing limits or manipulating results still apply."
          },
          {
            "type": "paragraph",
            "text": "Daily challenges reset at **00:00 UTC**."
          },
          {
            "type": "paragraph",
            "text": "CubitPlay may use server-side validation to verify cuts, scores, accuracy, attempt limits, and challenge identity."
          },
          {
            "type": "paragraph",
            "text": "Guest results may be eligible to be claimed after account creation or sign-in, subject to the rules shown in the game. If an account already has official attempts for that challenge, CubitPlay may keep those account attempts as authoritative rather than merging additional guest attempts."
          },
          {
            "type": "paragraph",
            "text": "When saving a guest profile to a new account, you may keep the generated nickname where available or choose another available nickname. Signing into an existing account keeps that account's nickname and imports eligible guest history without duplicating official attempts. Nicknames can later be changed in account settings. Guest recognition is browser/device-bound: clearing browser data or switching browsers/devices may create a new identity. We do not use fingerprinting to recover the prior identity."
          }
        ]
      },
      {
        "number": 7,
        "title": "Leaderboards",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Official leaderboards may display:"
          },
          {
            "type": "list",
            "items": [
              "your CubitPlay nickname;",
              "your score;",
              "your accuracy or amount off target;",
              "your rank; and",
              "other game-result information that is clearly shown as public."
            ]
          },
          {
            "type": "paragraph",
            "text": "Your email address is not displayed on public leaderboards."
          },
          {
            "type": "paragraph",
            "text": "For Daily Slice, rankings are currently determined by score first, then accuracy, and then completion time where a tie remains."
          },
          {
            "type": "paragraph",
            "text": "CubitPlay may correct, remove, or exclude results that are invalid, duplicated, manipulated, produced through technical error, or obtained in violation of these Terms."
          }
        ]
      },
      {
        "number": 8,
        "title": "Cheating and Manipulation",
        "blocks": [
          {
            "type": "paragraph",
            "text": "Fair play is part of the CubitPlay experience."
          },
          {
            "type": "paragraph",
            "text": "Do not manipulate requests, game data, scores, attempt limits, authentication, or leaderboard submissions to gain an unfair advantage."
          },
          {
            "type": "paragraph",
            "text": "Do not use scripts, bots, automation, modified clients, or other methods to create unfair leaderboard results."
          },
          {
            "type": "paragraph",
            "text": "Where we reasonably believe that a result or account violates these rules, we may remove affected results or restrict the account."
          },
          {
            "type": "paragraph",
            "text": "If you believe an action was taken in error, you may contact us at **hello@cubitplay.com**."
          }
        ]
      },
      {
        "number": 9,
        "title": "Account Restrictions and Suspension",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We may restrict, suspend, or terminate an account where reasonably necessary to:"
          },
          {
            "type": "list",
            "items": [
              "protect CubitPlay or other players;",
              "address fraud, abuse, cheating, or security risks;",
              "enforce these Terms; or",
              "comply with legal obligations."
            ]
          },
          {
            "type": "paragraph",
            "text": "Where appropriate, we may provide notice or an opportunity to contact us about the action."
          }
        ]
      },
      {
        "number": 10,
        "title": "Intellectual Property",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay, Perfect Slice, and the associated software, designs, artwork, branding, and content are owned by or licensed to the operator of CubitPlay unless otherwise stated."
          },
          {
            "type": "paragraph",
            "text": "We grant you a limited, personal, non-exclusive, non-transferable right to use CubitPlay for its intended purpose."
          },
          {
            "type": "paragraph",
            "text": "Unless permitted by law or by us in writing, you may not copy, reproduce, sell, redistribute, reverse engineer, or commercially exploit CubitPlay software, branding, artwork, or other protected content."
          },
          {
            "type": "paragraph",
            "text": "Third-party names, services, libraries, and trademarks remain the property of their respective owners."
          }
        ]
      },
      {
        "number": 11,
        "title": "Availability and Changes",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We aim to keep CubitPlay available and enjoyable, but games, features, leaderboards, accounts, challenges, or other parts of the service may change, be interrupted, or be discontinued."
          },
          {
            "type": "paragraph",
            "text": "We do not promise that any particular game, feature, score, ranking, or service will remain available permanently."
          },
          {
            "type": "paragraph",
            "text": "We may modify gameplay rules, scoring systems, challenge formats, or other features as the service evolves."
          }
        ]
      },
      {
        "number": 12,
        "title": "Account Deletion and Termination",
        "blocks": [
          {
            "type": "paragraph",
            "text": "You may stop using CubitPlay at any time."
          },
          {
            "type": "paragraph",
            "text": "If you delete your account, we will remove or de-identify account-linked information from active systems in accordance with our Privacy Policy, subject to information we may need to retain for security, legal, fraud-prevention, or operational reasons."
          },
          {
            "type": "paragraph",
            "text": "Deletion may also remove your saved game history and public leaderboard entries associated with the account."
          },
          {
            "type": "paragraph",
            "text": "Some local game data stored in your browser may remain until you clear it from your device."
          }
        ]
      },
      {
        "number": 13,
        "title": "Disclaimers",
        "blocks": [
          {
            "type": "paragraph",
            "text": "CubitPlay is provided on an **“as available”** basis."
          },
          {
            "type": "paragraph",
            "text": "We work to keep the service reliable and accurate, but we do not guarantee that CubitPlay will always be available, uninterrupted, error-free, or free from technical issues."
          },
          {
            "type": "paragraph",
            "text": "Scores, rankings, game results, and challenge availability may occasionally be affected by technical errors or service interruptions. We may correct affected results where reasonably necessary."
          },
          {
            "type": "paragraph",
            "text": "Nothing in these Terms excludes or limits rights that cannot legally be excluded or limited under applicable law."
          }
        ]
      },
      {
        "number": 14,
        "title": "Limitation of Liability",
        "blocks": [
          {
            "type": "paragraph",
            "text": "To the maximum extent permitted by applicable law, CubitPlay and its operator will not be responsible for indirect, incidental, special, consequential, or purely economic losses arising from use of or inability to use the service."
          },
          {
            "type": "paragraph",
            "text": "Nothing in these Terms limits liability where doing so would be unlawful, including liability that cannot be excluded under applicable consumer-protection law."
          }
        ]
      },
      {
        "number": 15,
        "title": "Changes to These Terms",
        "blocks": [
          {
            "type": "paragraph",
            "text": "We may update these Terms as CubitPlay evolves or as legal or operational requirements change."
          },
          {
            "type": "paragraph",
            "text": "When we make material changes, we will update the effective date above and may provide additional notice where appropriate."
          },
          {
            "type": "paragraph",
            "text": "Your continued use of CubitPlay after updated Terms take effect means the updated Terms apply to your future use of the service, subject to any additional consent requirements under applicable law."
          }
        ]
      },
      {
        "number": 16,
        "title": "Governing Law",
        "blocks": [
          {
            "type": "paragraph",
            "text": "These Terms are governed by the laws of India, subject to any mandatory rights you may have under the laws that apply to you."
          }
        ]
      },
      {
        "number": 17,
        "title": "Contact",
        "blocks": [
          {
            "type": "paragraph",
            "text": "For general questions about CubitPlay or these Terms, contact:"
          },
          {
            "type": "paragraph",
            "text": "**hello@cubitplay.com**"
          },
          {
            "type": "paragraph",
            "text": "For privacy-related questions, contact:"
          },
          {
            "type": "paragraph",
            "text": "**privacy@cubitplay.com**"
          }
        ]
      }
    ]
  }
};
